import React from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { updateTrackNodeAnnotation } from '../../features/layout/layoutSlice';
import type { TrackNodeLandmarkRole } from '../../types';
import {
  annotationForTrackNode,
  DEFAULT_GATE_FOUP_CAPACITY,
  trackNodeDisplayName,
} from '../../utils/trackNodeAnnotations';
import { formatSegmentDisplayNames } from '../../utils/simulationTrackNetworkView';
import type { SimulationTrackNodeView } from '../../utils/simulationTrackNetworkView';
import { useLayoutDisplayNameContext } from '../../hooks/useSegmentDisplayNameMap';
import { deleteSelectedTrackNode } from '../../utils/deleteCanvasSelection';
import { planRemoveTrackNode } from '../../utils/railInsert';
import { selectExportableEquipmentInstanceMap } from '../../features/equipment/equipmentSelectors';
import PropertySection from './PropertySection';
import {
  PropertyHint,
  PropertyInput,
  PropertyNumber,
  PropertyPanelHeader,
  PropertySelect,
  ReadOnlyRow,
} from './PropertyFields';

const ROLE_OPTIONS: { value: TrackNodeLandmarkRole; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'entry', label: 'Entry' },
  { value: 'exit', label: 'Exit' },
  { value: 'junction', label: 'Junction / Switch' },
];

type Props = {
  node: SimulationTrackNodeView;
};

/**
 * Properties for a selected simulation track node: display name and Entry/Exit role.
 * Only one Entry and one Exit node are allowed on the layout.
 */
const TrackNodeProperties: React.FC<Props> = ({ node }) => {
  const dispatch = useAppDispatch();
  const annotations = useAppSelector((state) => state.layout.trackNodeAnnotations);
  const equipmentInstances = useAppSelector(selectExportableEquipmentInstanceMap);
  const { elements, elementOrder, segmentDisplayNameMap } = useLayoutDisplayNameContext();
  const annotation = annotationForTrackNode(annotations, node);
  const canDeleteNode = planRemoveTrackNode(
    { x: node.x, y: node.y },
    Object.values(elements)
  ).ok;

  const patch = (
    changes: Partial<{ name: string; role: TrackNodeLandmarkRole; foupCapacity: number }>
  ) => {
    dispatch(
      updateTrackNodeAnnotation({
        x: node.x,
        y: node.y,
        defaultName: node.nodeId,
        changes,
      })
    );
  };

  const handleDelete = () => {
    if (!canDeleteNode) return;
    deleteSelectedTrackNode(
      dispatch,
      node.nodeId,
      elements,
      Object.values(equipmentInstances)
    );
  };

  const segmentText = formatSegmentDisplayNames(
    node.segmentIds,
    segmentDisplayNameMap,
    ', ',
    elements,
    elementOrder
  );
  const nodeLabel = trackNodeDisplayName(annotation, node.nodeId);
  const isGate = annotation.role === 'entry' || annotation.role === 'exit';
  const gateCapacity =
    typeof annotation.foupCapacity === 'number' && Number.isFinite(annotation.foupCapacity)
      ? annotation.foupCapacity
      : DEFAULT_GATE_FOUP_CAPACITY;

  return (
    <div className="p-2.5">
      <PropertyPanelHeader title={nodeLabel} subtitle="Track Node" />
      <div className="space-y-1.5">
        <PropertySection title="General">
          <PropertyInput label="Name" value={annotation.name} onChange={(name) => patch({ name })} />
          <PropertySelect
              label="Type"
              value={annotation.role}
              onChange={(value) => {
                const role = value as TrackNodeLandmarkRole;
                if (role === 'entry' || role === 'exit') {
                  const hasCapacity =
                    typeof annotation.foupCapacity === 'number' &&
                    Number.isFinite(annotation.foupCapacity) &&
                    annotation.foupCapacity > 0;
                  patch({
                    role,
                    ...(hasCapacity ? {} : { foupCapacity: DEFAULT_GATE_FOUP_CAPACITY }),
                  });
                  return;
                }
                patch({ role });
              }}
              options={ROLE_OPTIONS}
              hint="Only one Entry and one Exit node are allowed."
          />
        </PropertySection>

        {isGate && (
          <PropertySection title="Inventory">
              <PropertyNumber
                label="FOUP Capacity"
                min={1}
                step={1}
                value={gateCapacity}
                onChange={(value) => {
                  if (Number.isFinite(value) && value > 0) patch({ foupCapacity: value });
                }}
                hint={`Hold limit for this ${annotation.role === 'entry' ? 'Entry' : 'Exit'} gate.`}
              />
          </PropertySection>
        )}

        <PropertySection title="Track node">
          <ReadOnlyRow label="Name" value={nodeLabel} />
          <ReadOnlyRow
            label="Holding point"
            value={node.isHoldingPoint ? 'Yes' : 'No'}
          />
          <ReadOnlyRow label="Connected tracks" value={segmentText} />
          <ReadOnlyRow
            label="Position"
            value={`${node.x.toFixed(1)}, ${node.y.toFixed(1)}`}
          />
        </PropertySection>

        <PropertySection title="Actions">
          <button
            type="button"
            onClick={handleDelete}
            disabled={!canDeleteNode}
            title={
              canDeleteNode
                ? 'Delete this node and join the connected tracks'
                : 'Only mid-span nodes on a straight track can be deleted'
            }
            className="w-full rounded-md border border-red-200 bg-red-50 px-2 py-1 text-[11px] font-medium text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-400 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300 dark:hover:bg-red-950/70 dark:disabled:border-gray-700 dark:disabled:bg-gray-800 dark:disabled:text-gray-500"
          >
            Delete node
          </button>
          {!canDeleteNode && (
            <PropertyHint>
              This node can&apos;t be deleted because more than two tracks meet here, or the
              connected tracks aren&apos;t a straight mid-span join.
            </PropertyHint>
          )}
        </PropertySection>
      </div>
    </div>
  );
};

export default TrackNodeProperties;
